import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POINTS_KEY = "dolo-rewards-points";
const PLAYS_KEY = "dolo-rewards-plays";
const TAG_KEY = "dolopals-tag";
const DAILY_KEY = "dolo-daily-claim";

export const DAILY_POINTS = 15;

export const getLocalPoints = () => Number(localStorage.getItem(POINTS_KEY) || 0);

export const getLocalPlays = () => {
  try {
    return JSON.parse(localStorage.getItem(PLAYS_KEY)) || {};
  } catch {
    return {};
  }
};

export const getLinkedTag = () => localStorage.getItem(TAG_KEY);

export const setLinkedTag = (tag) => localStorage.setItem(TAG_KEY, tag);

const setLocalPoints = (total) => localStorage.setItem(POINTS_KEY, String(total));

/* force the local balance (server is authoritative after a cloud spend) */
export const overrideLocalPoints = (total) => {
  setLocalPoints(total);
  window.dispatchEvent(new CustomEvent("dolo-rewards", { detail: { total, game: null, earned: 0 } }));
};

/* local-only spend (guests without a linked tag) — false when the stash is short */
export const spendLocalPoints = (amount) => {
  const current = getLocalPoints();
  if (current < amount) return false;
  overrideLocalPoints(current - amount);
  return true;
};

/* Award points for a game action — always banks locally, and when a Titan Tag
   is linked it also fires a background cloud earn so the vault stays in sync. */
export const awardPoints = (game, points) => {
  if (!Number.isFinite(points) || points <= 0) return getLocalPoints();
  const earned = Math.min(50, Math.floor(points));
  const total = getLocalPoints() + earned;
  setLocalPoints(total);
  const plays = getLocalPlays();
  plays[game] = (plays[game] || 0) + 1;
  localStorage.setItem(PLAYS_KEY, JSON.stringify(plays));
  window.dispatchEvent(new CustomEvent("dolo-rewards", { detail: { total, game, earned } }));
  const tag = getLinkedTag();
  if (tag) {
    axios.post(`${API}/rewards/earn/${encodeURIComponent(tag)}`, { game, points: earned }).catch(() => {});
  }
  return total;
};

export const fetchCloudPoints = async (tag) => {
  const { data } = await axios.get(`${API}/rewards/${encodeURIComponent(tag)}`);
  return data;
};

export const fetchTopRunners = async () => {
  const { data } = await axios.get(`${API}/rewards/top`);
  return data;
};

export const fetchCrowHunters = async () => {
  const { data } = await axios.get(`${API}/rewards/crow-hunters`);
  return data;
};

export const fetchHallOfFame = async () => {
  const { data } = await axios.get(`${API}/rewards/halloffame`);
  return data;
};

export const fetchDeliveryRecords = async () => {
  const { data } = await axios.get(`${API}/wasteland/delivery/leaderboard`);
  return data;
};

export const mintTag = async () => {
  const { data } = await axios.post(`${API}/pals/tag`);
  setLinkedTag(data.tag);
  return data.tag;
};

/* Push the local stash up — the vault keeps the max, so nothing is ever lost */
export const syncPointsToTag = async (tag) => {
  const { data } = await axios.put(`${API}/rewards/sync/${encodeURIComponent(tag)}`, { points: getLocalPoints() });
  return data.points;
};

/* Pull the vault down — adopt the higher balance so a second device catches up */
export const adoptCloudPoints = async (tag) => {
  const data = await fetchCloudPoints(tag);
  if (Number.isFinite(data.points) && data.points > getLocalPoints()) {
    setLocalPoints(data.points);
    window.dispatchEvent(new CustomEvent("dolo-rewards", { detail: { total: data.points, game: null, earned: 0 } }));
  }
  return data;
};

/* ---------- daily Bio Hack ration + streaks ---------- */
const todayStamp = () => new Date().toISOString().slice(0, 10);
const yesterdayStamp = () => new Date(Date.now() - 86400000).toISOString().slice(0, 10);

/* consecutive-day bonus: +5 per streak day past the first, capped at +30 (day 7+) */
export const dailyBonusFor = (streak) => Math.min(Math.max(streak - 1, 0), 6) * 5;

export const getDailyState = () => {
  try {
    return JSON.parse(localStorage.getItem(DAILY_KEY)) || {};
  } catch {
    return {};
  }
};

export const isDailyClaimable = () => getDailyState().date !== todayStamp();

export const getTubeCount = () => getDailyState().tubes || 0;

/* the streak standing today (alive if claimed today or yesterday) */
export const getDailyStreak = () => {
  const s = getDailyState();
  if (s.date === todayStamp() || s.date === yesterdayStamp()) return s.streak || 0;
  return 0;
};

/* the streak day this incoming claim will land on */
export const peekDailyStreak = () => {
  const s = getDailyState();
  return s.date === yesterdayStamp() ? (s.streak || 0) + 1 : 1;
};

/* Claim today's free Bio Hack tube. With a linked tag the server is
   authoritative (one claim per UTC day across every device, streak counted
   server-side); anonymous players are gated by the local day stamp.
   Returns null when already claimed. */
export const claimDaily = async () => {
  if (!isDailyClaimable()) return null;
  const prev = getDailyState();
  const tubes = (prev.tubes || 0) + 1;
  const tag = getLinkedTag();
  if (tag) {
    try {
      const { data } = await axios.post(`${API}/rewards/daily/${encodeURIComponent(tag)}`);
      const earned = data.earned || DAILY_POINTS;
      const streak = data.streak || 1;
      localStorage.setItem(DAILY_KEY, JSON.stringify({ date: todayStamp(), tubes, streak }));
      const total = Math.max(getLocalPoints() + earned, data.points);
      setLocalPoints(total);
      window.dispatchEvent(new CustomEvent("dolo-rewards", { detail: { total, game: "daily", earned } }));
      return { total, tubes, streak, earned };
    } catch (error) {
      if (error.response?.status === 409) {
        // claimed on another device — lock today's local claim too
        localStorage.setItem(DAILY_KEY, JSON.stringify({ date: todayStamp(), tubes: prev.tubes || 0, streak: prev.streak || 0 }));
        return null;
      }
      // network hiccup — fall through to the local-only award
    }
  }
  const streak = prev.date === yesterdayStamp() ? (prev.streak || 0) + 1 : 1;
  const earned = DAILY_POINTS + dailyBonusFor(streak);
  localStorage.setItem(DAILY_KEY, JSON.stringify({ date: todayStamp(), tubes, streak }));
  const total = awardPoints("daily", earned);
  return { total, tubes, streak, earned };
};

/* If the vault says today's ration is already gone (claimed on another
   device), lock the local claim so the popup stops offering it. */
export const syncDailyFromCloud = async (tag) => {
  try {
    const data = await fetchCloudPoints(tag);
    if (data.last_daily === todayStamp() && getDailyState().date !== todayStamp()) {
      const prev = getDailyState();
      localStorage.setItem(
        DAILY_KEY,
        JSON.stringify({
          date: todayStamp(),
          tubes: Math.max(prev.tubes || 0, data.daily_claims || 1),
          streak: data.streak || prev.streak || 1,
        }),
      );
      return true;
    }
  } catch {
    /* offline — local state rules */
  }
  return false;
};

/* Spend one stocked tube (Wasteland Run energy boost). Returns false when dry. */
export const spendTube = () => {
  const state = getDailyState();
  const tubes = state.tubes || 0;
  if (tubes <= 0) return false;
  localStorage.setItem(DAILY_KEY, JSON.stringify({ ...state, tubes: tubes - 1 }));
  window.dispatchEvent(new CustomEvent("dolo-tubes", { detail: { tubes: tubes - 1 } }));
  return true;
};

/* Forge an achievement feat badge on the linked Titan Tag (HAT TRICK,
   LEGENDARY PULL, CROWN TAKER, KILOMETER CLUB). Toasts only on first forge. */
export const awardFeat = async (badge) => {
  const tag = getLinkedTag();
  if (!tag) return null;
  try {
    const { data } = await axios.post(`${API}/rewards/badge/${encodeURIComponent(tag)}`, { badge });
    if (data.added) toast.success(`BADGE FORGED — ${badge}.`);
    window.dispatchEvent(new CustomEvent("dolo-badge", { detail: { badge, badges: data.badges } }));
    return data.badges;
  } catch {
    return null;
  }
};
